import { showReportDialog } from '@sentry/react';

import { Button } from '@surface/components';

type ErrorReportProps = {
  error: Error;
  eventId?: string;
};

/**
 * Displays an error message, and allows the user to submit additional feedback via Sentry.
 *
 * @component
 * @param {Object} props
 * @param {Error} props.error - The error object to display
 * @param {string} [props.eventId] - Optional Sentry event ID for error tracking, this is provided by Sentry's ErrorBoundary component
 */

export const ErrorReport = ({ error, eventId }: ErrorReportProps) => {
  return (
    <div
      data-testid="error-report"
      className="min-h-[400px] p-8 flex flex-col items-center justify-center text-center"
    >
      <div className="max-w-md space-y-6">
        <div className="bg-white p-6 rounded-xl border border-gray-50 shadow-md">
          <h2
            data-testid="error-report-title"
            className="text-xl font-semibold mb-2"
          >
            Something went wrong 😞
          </h2>
          <p data-testid="error-report-message" className="text-gray-600 mb-4">
            Looks like this unit needs some repairs! Our team has been notified.
            If you would like to add notes to the work order we generate please
            use the button below.
          </p>
          <div className="space-y-2 text-left bg-white p-4 rounded-lg border border-gray-200">
            <p className="text-sm font-medium">Maintenance Report:</p>
            <p
              data-testid="error-report-details"
              className="text-sm text-gray-600 font-mono break-all"
            >
              {error.message}
            </p>
          </div>
        </div>

        <Button
          data-testid="error-report-feedback-button"
          onClick={() =>
            showReportDialog({
              eventId,
              title: 'Maintenance Request',
              subtitle: 'Help us fix this issue by providing more details',
            })
          }
        >
          Submit additional feedback
        </Button>

        {eventId && (
          <p
            data-testid="error-report-reference"
            className="text-sm text-gray-500"
          >
            Reference #: {eventId?.slice(0, 8)}
          </p>
        )}
      </div>
    </div>
  );
};
